import { Key, FileText, Bot, Building2, CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ModuleAccessKey } from "../../lib/accessRequests";

export type AccessGatePlaceholderVariant =
  | "dashboard-card"
  | "page-documents"
  | "page-ai-chat"
  | "page-generic"
  | "settings-rows"
  | "api-table"
  | "inline";

type PlaceholderProps = {
  variant: AccessGatePlaceholderVariant;
  module: ModuleAccessKey;
  title?: string;
  className?: string;
};

function SkeletonBar({ className }: { className?: string }) {
  return (
    <div
      className={`h-3 rounded-md bg-gray-200 dark:bg-gray-700 ${className ?? ""}`}
      aria-hidden
    />
  );
}

export function AccessGatePlaceholder({ variant, module, title, className }: PlaceholderProps) {
  const { t } = useTranslation();
  const moduleTitle =
    title ?? t(`accessRequests.modules.${module}`, { defaultValue: module });

  if (variant === "dashboard-card") {
    const Icon = module === "payments" ? CreditCard : Building2;
    return (
      <div
        className={
          className ??
          "card p-6 space-y-4 border border-gray-200 dark:border-gray-700"
        }
        aria-hidden
        inert
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-300 dark:text-gray-600" />
          <SkeletonBar className="w-32 h-4" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-gray-100 dark:border-gray-800 p-3 space-y-2">
              <SkeletonBar className="w-20" />
              <SkeletonBar className="w-12 h-5" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "page-documents") {
    return (
      <div className={className ?? "space-y-4"} aria-hidden inert>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-2">
            <SkeletonBar className="w-48 h-6" />
            <SkeletonBar className="w-64" />
          </div>
          <SkeletonBar className="w-28 h-9" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonBar key={i} className="w-24 h-8" />
          ))}
        </div>
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex gap-3">
            <FileText className="w-5 h-5 text-gray-300 shrink-0" />
            <SkeletonBar className="flex-1 max-w-md" />
          </div>
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0"
            >
              <SkeletonBar className="w-8 h-8 rounded-lg" />
              <div className="flex-1 space-y-2">
                <SkeletonBar className="w-2/3 max-w-sm" />
                <SkeletonBar className="w-1/3 max-w-[8rem]" />
              </div>
              <SkeletonBar className="w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "page-generic") {
    return (
      <div className={className ?? "space-y-4 p-2"} aria-hidden inert>
        <SkeletonBar className="w-56 h-7" />
        <SkeletonBar className="w-full max-w-lg" />
        <div className="card p-4 space-y-3 mt-4">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonBar key={i} className="w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (variant === "page-ai-chat") {
    return (
      <div
        className={className ?? "flex flex-col items-center justify-center min-h-[50vh] gap-6"}
        aria-hidden
        inert
      >
        <div className="flex items-center gap-3">
          <Bot className="w-10 h-10 text-gray-300 dark:text-gray-600" />
          <SkeletonBar className="w-40 h-7" />
        </div>
        <SkeletonBar className="w-56 h-4" />
        <SkeletonBar className="w-36 h-10 rounded-lg" />
      </div>
    );
  }

  if (variant === "settings-rows") {
    return (
      <div className={className ?? "space-y-4"} aria-hidden inert>
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-4 py-3 border-b border-gray-100 dark:border-gray-800"
          >
            <div className="space-y-2 flex-1">
              <SkeletonBar className="w-40" />
              <SkeletonBar className="w-full max-w-md" />
            </div>
            <SkeletonBar className="w-11 h-6 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "api-table") {
    return (
      <div className={className ?? "space-y-4"} aria-hidden inert>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <SkeletonBar className="w-36 h-5" />
            <SkeletonBar className="w-64" />
          </div>
          <SkeletonBar className="w-32 h-9 rounded-lg" />
        </div>
        <div className="card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-gray-300" />
            <SkeletonBar className="w-48" />
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="grid grid-cols-4 gap-3">
              <SkeletonBar />
              <SkeletonBar />
              <SkeletonBar />
              <SkeletonBar className="w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className ?? "h-24 rounded-xl bg-gray-100 dark:bg-gray-800"} aria-hidden inert>
      <span className="sr-only">{moduleTitle}</span>
    </div>
  );
}
